import type { Ref } from 'react'
import { useLayoutEffect, useRef, useState } from 'react'
import { IconCartGlyph } from '../../../components/icons'
import { useToast } from '../../../components/ui/ToastProvider'
import { useCartCount } from '../../../lib/cart/cart-count-context'
import { formatMoney } from '../../../lib/format/money'
import { addCatalogResultToCart } from '../catalog-add-to-cart'
import { resultDisplayName } from '../format'
import type { CatalogResultCard } from '../types'
import { useRecentlyViewed } from '../useRecentlyViewed'
import { CatalogResultThumb } from './CatalogResultThumb'
import { RecentlyViewedQuickViewModal } from './RecentlyViewedQuickViewModal'

export interface RecentlyViewedProps {
  /**
   * Same seam `HomeCategoryCarousel` exposes (ADR-0128 Decision 5): this
   * component forwards the scroll container's plain DOM ref rather than
   * rendering its own Prev/Next chrome, so the PARENT (`CatalogBrowsePage`)
   * can put the arrows in its own section-heading row — matching the
   * design source's placement (`Fictiv Platform.dc.html` ~L843-848, the
   * arrows sit beside the "Recently viewed" caption) and the Home carousel's
   * existing division of responsibility, rather than a second, divergent
   * carousel implementation.
   */
  readonly ref?: Ref<HTMLDivElement>
}

/**
 * The `/catalog` "Recently viewed" row — REAL, per-user, 100% client-side
 * view-history tracking (the revived alternative to ADR-0128's static
 * curated row; ADR-0127's server-side design was rejected on cost for a
 * demo, but this keeps the "real tracking" intent while staying entirely
 * client-local — see `recently-viewed-store.ts`/`useRecentlyViewed.ts`).
 * Renders whatever `useRecentlyViewed()` reads back for the signed-in user
 * (most-recently-viewed first, capped at 8) as the design source's own
 * compact tile — a small square thumbnail, name/SKU/price stacked beside
 * it, and an icon-only "Add to cart" square button — NOT the full grid
 * `ResultCard` tile (that first pass over-sized the row; the design's
 * actual tile is ~244px wide and ~70px tall, one line of info, not a full
 * product card).
 *
 * The click-to-open-quick-view affordance is a full-tile-cover `<button>`
 * UNDER the tile's content in z-order (`.catalog-recently-viewed-open`,
 * catalog.css) — a real, sibling `<button>`, not a `<div onClick>` or an
 * ARIA `role="button"` wrapper, and NOT nested inside the "Add" button
 * (invalid interactive-in-interactive nesting). CSS lifts the Add button
 * above the overlay in z-index, so both are independently reachable by
 * mouse and by keyboard Tab order with their own accessible names —
 * clicking anywhere on the tile except "Add" opens the quick-view modal;
 * clicking "Add" adds to cart and never opens the modal. There is no
 * route/PDP for a finalized catalog SKU, so this modal is the row's only
 * click-through destination. Opening the quick-view modal from THIS row is
 * a pure UI action with no tracking side effect — recording it would
 * reorder the row under the shopper's cursor while they browse their own
 * history.
 *
 * No loading state and — the user's explicit call — no curated fallback and
 * no empty-state message either: renders NOTHING (not even the "Recently
 * viewed" heading, which lives one level up in `CatalogBrowsePage.tsx`, so
 * that page must consult `useRecentlyViewed()` too before deciding whether
 * to render its heading/Prev-Next chrome) until the shopper has actually
 * viewed something real.
 */
export function RecentlyViewed({ ref }: RecentlyViewedProps) {
  const entries = useRecentlyViewed()
  const [openCard, setOpenCard] = useState<CatalogResultCard | null>(null)

  if (entries.length === 0) return null

  return (
    <>
      <div className="catalog-recently-viewed-scroll" ref={ref}>
        {entries.map((entry) => {
          const card: CatalogResultCard = {
            sourceable: entry.sourceable,
            availability: entry.availability,
            ...(entry.category !== undefined ? { category: entry.category } : {}),
          }
          return (
            <RecentlyViewedTile
              key={entry.sourceable.id}
              card={card}
              onOpen={() => setOpenCard(card)}
            />
          )
        })}
      </div>
      <RecentlyViewedQuickViewModal card={openCard} onClose={() => setOpenCard(null)} />
    </>
  )
}

function RecentlyViewedTile({
  card,
  onOpen,
}: {
  readonly card: CatalogResultCard
  readonly onOpen: () => void
}) {
  const { sourceable } = card
  const name = resultDisplayName(sourceable)
  const nameRef = useRef<HTMLParagraphElement>(null)
  const [isNameTruncated, setIsNameTruncated] = useState(false)

  useLayoutEffect(() => {
    const el = nameRef.current
    if (!el) return
    setIsNameTruncated(el.scrollWidth > el.clientWidth)
  }, [])

  return (
    <div className="catalog-recently-viewed-tile">
      <button
        type="button"
        className="catalog-recently-viewed-open"
        aria-label={`View ${name}`}
        title={isNameTruncated ? name : undefined}
        onClick={onOpen}
      />
      <CatalogResultThumb
        imageRef={sourceable.imageRef}
        name={name}
        className="catalog-result-image-recently-viewed"
      />
      <div className="catalog-recently-viewed-info">
        <p className="catalog-recently-viewed-name" ref={nameRef}>
          {name}
        </p>
        {sourceable.catalogSku && (
          <p className="catalog-recently-viewed-sku">{sourceable.catalogSku}</p>
        )}
        {sourceable.indicativePrice ? (
          <p className="catalog-recently-viewed-price">{formatMoney(sourceable.indicativePrice)}</p>
        ) : (
          <p className="catalog-recently-viewed-price catalog-recently-viewed-price-quote">
            Quote required
          </p>
        )}
      </div>
      <RecentlyViewedAddButton sourceable={sourceable} name={name} />
    </div>
  )
}

function RecentlyViewedAddButton({
  sourceable,
  name,
}: {
  readonly sourceable: CatalogResultCard['sourceable']
  readonly name: string
}) {
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()
  const [adding, setAdding] = useState(false)

  async function handleAdd() {
    setAdding(true)
    try {
      const result = await addCatalogResultToCart(sourceable)
      if (result.lineCount === null) return // 401 — handled globally.
      setLineCount(result.lineCount)
      showToast(`Added ${name} to cart`)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not add that item.', 'error')
    } finally {
      setAdding(false)
    }
  }

  return (
    <button
      type="button"
      className="catalog-recently-viewed-add"
      aria-label={`Add ${name} to cart`}
      disabled={adding}
      onClick={() => void handleAdd()}
    >
      <IconCartGlyph />
    </button>
  )
}
